import { flattenObject } from 'volta/utils/object-utils';
import moment from 'moment-timezone';
import { isArray } from '@ember/array';
import _isObject from 'lodash/isObject';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type IntlService from 'ember-intl/services/intl';
import type LabelService from 'volta/services/label-service';
import type SessionUserService from 'volta/services/session-user';
import type Activity from 'volta/models/activity';
import type LocalizationService from 'volta/services/localization';
import { diff } from 'volta/utils/array-utils';
import { isDate } from 'volta/utils/date-utils';
import Task from 'volta/models/task';

type TFieldType =
  | 'number'
  | 'string'
  | 'date'
  | 'array'
  | 'custom'
  | 'link'
  | 'boolean'
  | 'DAFWeekly'
  | 'DAFMonthly';
const FIELD_TYPES = [
  'number',
  'string',
  'date',
  'array',
  'custom',
  'link',
  'boolean',
  'DAFWeekly',
  'DAFMonthly'
];

const transformDiffPayload = (payload: Record<string, any> = {}) => {
  const transformField = (type: TFieldType, field: 'string') =>
    type === 'date' ? new Date(field) : field;
  let newPayload: Record<string, any> = {};

  Object.keys(payload).forEach((key) => {
    const field = payload[key] ?? '';

    if (isArray(field)) {
      newPayload[`${key}_length`] = field.length;
    }

    if (field.type) {
      if (field.value !== null) {
        newPayload[`${key}_new`] = transformField(field.type, field.value);
      }

      if (field.oldValue !== null) {
        newPayload[`${key}_old`] = transformField(field.type, field.oldValue);
      }
    } else {
      newPayload[key] = isDate(field) ? moment(field).toDate() : field;
    }
  });
  return newPayload;
};

const transformConfigsPayload = (value: Record<string, any> = {}) => {
  let attrs: Record<string, { type: string; value: any }> = {};
  Object.keys(value).forEach((key) => {
    let attrType = 'string';
    let attrValue = value[key];
    if (attrValue) {
      if (isArray(attrValue)) {
        attrType = 'array';
      } else if (typeof attrValue === 'object') {
        attrType = 'custom';
      } else {
        attrType = typeof attrValue;
      }
    }
    attrs[key] = {
      type: attrType,
      value: attrValue
    };
  });
  return attrs;
};

interface IArgs {
  /* The activity model */
  activity: Activity;
  selected: boolean;
}

export default class ActivityItem extends Component<IArgs> {
  @service intl!: IntlService;
  @service labelService!: LabelService;
  @service sessionUser!: SessionUserService;
  @service localization!: LocalizationService;

  @tracked showDetails: boolean = false;
  @tracked dafYear: string = `${new Date().getFullYear()}`;

  /**
   * The current logged in user id
   */
  get userId() {
    return this.sessionUser.user?.userId;
  }

  get userName() {
    return this.userId === this.args.activity.userId && this.intl.primaryLocale.includes('en')
      ? this.intl.t('you')
      : this.args.activity.userName;
  }

  get command() {
    return this.args.activity.command.toLowerCase();
  }

  get commandClass() {
    const { command } = this;
    if (command.indexOf('create') > -1) {
      return 'activity-item--create';
    } else if (command.indexOf('update') > -1) {
      return 'activity-item--update';
    } else if (command.indexOf('delete') > -1) {
      return 'activity-item--delete';
    } else {
      return 'activity-item--command';
    }
  }

  /**
   * The activity description label attributes
   */
  get labelAttrs() {
    const { activity } = this.args;
    const { payload = {} } = activity;
    let attrs: Record<string, string> = flattenObject(
      Object.assign({}, _isObject(payload) ? payload : {})
    );

    if (attrs) {
      attrs['userName'] = activity.userName;
      attrs['entityIds'] = activity.entityIds.join(',');

      if ((!payload || !payload.entityId) && activity.entityId) {
        attrs['entityId'] = activity.entityId;
      }

      for (const key in attrs) {
        if (Object.prototype.hasOwnProperty.call(attrs, key)) {
          if (key.includes('unitMeasure')) {
            attrs[key] = this.labelService.unitMeasure(attrs[key]);
          }

          if (key === 'taskType') {
            attrs[key] = this.localization.tOrElse(attrs[key]) as string;
          }
        }
      }
      attrs = transformDiffPayload(attrs);
    }

    return attrs;
  }

  get updateAttrs() {
    const { payload = {} } = this.args.activity;
    const keys = Object.keys(payload);
    let updateAttrs: Record<string, any> = {};
    if (
      this.args.activity.entityType === 'configs' &&
      payload['key'] &&
      typeof payload.value === 'object'
    ) {
      if (isArray(payload.value)) {
        payload.value = { [payload.key]: payload.value };
      }
      updateAttrs = transformConfigsPayload(payload['value']) as Record<string, any>;
    } else {
      keys.forEach((key) => {
        const field = payload[key];
        const attrs = updateAttrs[key] ?? {};
        if (
          field &&
          field.type &&
          (isPresent(field.value) || isPresent(field.oldValue)) &&
          FIELD_TYPES.includes(field.type)
        ) {
          attrs.type = field.type;

          if (field.type === 'array') {
            // Removed
            attrs.oldValue = diff(field.value ?? [], field.oldValue ?? []);
            // Added
            attrs.value = diff(field.oldValue ?? [], field.value ?? []);
          } else {
            (attrs.value = field.type === 'date' ? new Date(field.value) : field.value),
              (attrs.oldValue = isPresent(field.oldValue)
                ? field.type === 'date'
                  ? new Date(field.oldValue)
                  : field.oldValue
                : null);
          }
          updateAttrs[key] = attrs;
        }
      });
    }
    return updateAttrs;
  }

  get updateAttrsKeys() {
    return Object.keys(this.updateAttrs);
  }

  /**
   * The activity description label
   */
  get activityDescription() {
    const { activity } = this.args;
    const { taskType } = activity.payload as Task;
    const baseKey = `activitiesPage.${activity.entityType}.${activity.command}`;
    const augmentedKey = `${baseKey}${taskType ? `_${taskType}` : ''}`;
    return activity.entityType
      ? this.localization.tOrElse(
          augmentedKey,
          this.localization.tOrElse(baseKey, baseKey, this.labelAttrs) as string,
          this.labelAttrs
        )
      : undefined;
  }
}
